/**
 * Pricing Calculator Utility
 * 
 * Shared utility for calculating pricing summaries from quotation results.
 * Used by both ProtectionLevelStepNew (Page 5) and SummaryApprovalStepNew (Page 7)
 * to ensure consistent pricing calculations across the wizard.
 */

import type { QuoteWizardData } from '../QuoteWizard';
import type { QuotationAggregates } from '../../../types/quote';

/**
 * Format a number as currency with thousands separator and two decimal places
 */
export function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString('en-US', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;
}

export interface PricingBreakdown {
  anrp: number;
  grp: number;
  loadings: number;
  tax: number;
  subsidy: number;
  total: number;
  paymentPerMm: number;
  strikeThreshold: string;
  exhaustThreshold: string;
}

export interface ProtectionLevelSlider {
  peril: string;
  perilName: string;
  availableOptions: Array<{
    strike: number;
    exhaust: number;
    premium: number;
    totalPayable: number;
    anrp: number;
    grp: number;
    taxAmount: number;
    subsidyAmount: number;
    strikeThreshold: number;
    exhaustThreshold: number;
    paymentPerMm: number;
  }>;
}

/**
 * Calculate pricing breakdown from stable data (protectionLevels + quotationResults)
 * This is the authoritative pricing calculation used across the wizard
 */
export function calculatePricingFromStableData(
  data: QuoteWizardData
): PricingBreakdown {
  const protectionLevels = data.quote?.protectionLevels;
  const quotationResults = data.quote?.quotationResults;

  if (!protectionLevels || !quotationResults) {
    return {
      anrp: 0,
      grp: 0,
      loadings: 0,
      tax: 0,
      subsidy: 0,
      total: 0,
      paymentPerMm: 0,
      strikeThreshold: 'N/A',
      exhaustThreshold: 'N/A'
    };
  }

  try {
    const results = quotationResults as Record<string, unknown>;
    
    // Extract scenarios from quotationResults
    const allScenarios: Record<string, unknown>[] = [];
    
    // Try processedSliders format first
    if (results.processedSliders && Array.isArray(results.processedSliders)) {
      const sliders = results.processedSliders as Record<string, unknown>[];
      sliders.forEach(slider => {
        if (slider.availableOptions && Array.isArray(slider.availableOptions)) {
          allScenarios.push(...(slider.availableOptions as Record<string, unknown>[]));
        }
      });
    }
    
    // Fallback to raw quotation_result format
    if (allScenarios.length === 0 && results.output) {
      const output = results.output as Record<string, unknown>;
      if (output.quotation_result && Array.isArray(output.quotation_result)) {
        const quotationResult = output.quotation_result as Record<string, unknown>[];
        quotationResult.forEach(farmer => {
          if (farmer.data && Array.isArray(farmer.data)) {
            allScenarios.push(...(farmer.data as Record<string, unknown>[]));
          }
        });
      }
    }

    if (allScenarios.length === 0) {
      return {
        anrp: 0,
        grp: 0,
        loadings: 0,
        tax: 0,
        subsidy: 0,
        total: 0,
        paymentPerMm: 0,
        strikeThreshold: 'N/A',
        exhaustThreshold: 'N/A'
      };
    }

    // Find selected scenarios based on protectionLevels
    const selectedScenarios: Record<string, unknown>[] = [];
    
    Object.entries(protectionLevels).forEach(([peril, levels]) => {
      const level = levels as unknown as Record<string, unknown>;
      const strikeLevel = level.strike_level || level.strikeLevel;
      const exhaustLevel = level.exhaust_level || level.exhaustLevel;
      
      // Find matching scenario in allScenarios
      const matchingScenario = allScenarios.find(scenario => {
        // Check both formats: scenario.strike/exhaust and peril-specific fields
        const scenarioStrike = scenario.strike || scenario[`${peril}_strike_return_period`];
        const scenarioExhaust = scenario.exhaust || scenario[`${peril}_exhaust_return_period`];
        
        return scenarioStrike === strikeLevel && scenarioExhaust === exhaustLevel;
      });
      
      if (matchingScenario) {
        selectedScenarios.push(matchingScenario);
      }
    });

    if (selectedScenarios.length === 0) {
      return {
        anrp: 0,
        grp: 0,
        loadings: 0,
        tax: 0,
        subsidy: 0,
        total: 0,
        paymentPerMm: 0,
        strikeThreshold: 'N/A',
        exhaustThreshold: 'N/A'
      };
    }

    // Calculate totals from selected scenarios
    const totalAnrp = selectedScenarios.reduce((sum, scenario) => sum + ((scenario.anrp || scenario.ANRP) as number || 0), 0);
    const totalGrp = selectedScenarios.reduce((sum, scenario) => sum + ((scenario.grp || scenario.GRP) as number || 0), 0);
    const totalTax = selectedScenarios.reduce((sum, scenario) => sum + ((scenario.taxAmount || scenario.tax_amount) as number || 0), 0);
    const totalSubsidy = selectedScenarios.reduce((sum, scenario) => sum + ((scenario.subsidyAmount || scenario.subsidy_amount) as number || 0), 0);
    const totalPayable = selectedScenarios.reduce((sum, scenario) => sum + ((scenario.totalPayable || scenario.total_payable) as number || 0), 0);
    const totalPaymentPerMm = selectedScenarios.reduce((sum, scenario) => sum + ((scenario.paymentPerMm || scenario.payment_per_mm) as number || 0), 0);

    // Format thresholds
    const strikeThresholds = selectedScenarios.map(scenario => ((scenario.strikeThreshold || scenario.strike_threshold) as number || 0).toFixed(1));
    const exhaustThresholds = selectedScenarios.map(scenario => ((scenario.exhaustThreshold || scenario.exhaust_threshold) as number || 0).toFixed(1));

    return {
      anrp: totalAnrp,
      grp: totalGrp,
      loadings: totalGrp - totalAnrp,
      tax: totalTax,
      subsidy: totalSubsidy,
      total: totalPayable,
      paymentPerMm: totalPaymentPerMm,
      strikeThreshold: strikeThresholds.join(', ') + ' unit',
      exhaustThreshold: exhaustThresholds.join(', ') + ' unit'
    };
  } catch (error) {
    console.error('Error calculating pricing from stable data:', error);
    return {
      anrp: 0,
      grp: 0,
      loadings: 0,
      tax: 0,
      subsidy: 0,
      total: 0,
      paymentPerMm: 0,
      strikeThreshold: 'N/A',
      exhaustThreshold: 'N/A'
    };
  }
}

/**
 * Fallback pricing calculation from pricingCalculation data
 * Used when protection sliders are not available
 */
export function calculatePricingFromPricingCalculation(
  pricingCalculation: Record<string, unknown>
): PricingBreakdown {
  if (!pricingCalculation || !pricingCalculation.totalPremium) {
    return {
      anrp: 0,
      grp: 0,
      loadings: 0,
      tax: 0,
      subsidy: 0,
      total: 0,
      paymentPerMm: 0,
      strikeThreshold: 'N/A',
      exhaustThreshold: 'N/A'
    };
  }

  const breakdown = (pricingCalculation.breakdown as Record<string, unknown>) || {};
  return {
    anrp: (breakdown.anrp as number) || 0,
    grp: ((breakdown.anrp as number) || 0) + ((breakdown.loadings as number) || 0),
    loadings: (breakdown.loadings as number) || 0,
    tax: (breakdown.taxes as number) || 0,
    subsidy: (breakdown.subsidies as number) || 0,
    total: (pricingCalculation.totalPremium as number) || 0,
    paymentPerMm: 0, // Not available in pricingCalculation
    strikeThreshold: 'N/A',
    exhaustThreshold: 'N/A'
  };
}

/**
 * Extract pricing breakdown from backend aggregates (preferred method)
 * Uses pre-calculated aggregates from backend for accurate pricing
 */
export function getPricingFromAggregates(aggregates: QuotationAggregates): PricingBreakdown {
  const total = aggregates.total;
  
  return {
    anrp: total.ANRP,
    grp: total.GRP,
    loadings: total.GRP - total.ANRP,
    tax: total.tax_amount,
    subsidy: total.subsidy_amount,
    total: total.total_payable,
    paymentPerMm: total.payment_per_mm,
    strikeThreshold: `${total.strike_threshold.toFixed(1)}mm`,
    exhaustThreshold: `${total.exhaust_threshold.toFixed(1)}mm`
  };
}

/**
 * Get pricing breakdown with automatic fallback logic
 * Prioritizes stable data over UI state for consistent calculations
 */
export function getPricingBreakdown(
  _protectionSliders: ProtectionLevelSlider[] | undefined,
  data: QuoteWizardData,
  aggregates?: QuotationAggregates
): PricingBreakdown {
  // First try: Use backend aggregates (most accurate and efficient)
  if (aggregates) {
    return getPricingFromAggregates(aggregates);
  }

  // Second try: Use pricingCalculation data (fallback for compatibility)
  if (data.pricingCalculation) {
    const result = calculatePricingFromPricingCalculation(data.pricingCalculation as Record<string, unknown>);
    if (result.total > 0) {
      return result;
    }
  }

  // Third try: Calculate from stable data (legacy approach)
  const stableResult = calculatePricingFromStableData(data);
  if (stableResult.total > 0) {
    return stableResult;
  }

  // Final fallback: Use quote premiumAmount
  const premiumAmount = typeof data.quote?.premiumAmount === 'string' ? 
    parseFloat(data.quote.premiumAmount) : (data.quote?.premiumAmount || 0);
    
  if (premiumAmount > 0) {
    return {
      anrp: premiumAmount * 0.7, // Estimate
      grp: premiumAmount,
      loadings: premiumAmount * 0.3, // Estimate
      tax: 0,
      subsidy: 0,
      total: premiumAmount,
      paymentPerMm: 0,
      strikeThreshold: 'N/A',
      exhaustThreshold: 'N/A'
    };
  }

  // No pricing data available
  return {
    anrp: 0,
    grp: 0,
    loadings: 0,
    tax: 0,
    subsidy: 0,
    total: 0,
    paymentPerMm: 0,
    strikeThreshold: 'N/A',
    exhaustThreshold: 'N/A'
  };
}
